import React, { useContext } from "react";
import styled, { keyframes } from "styled-components";
import { ProductContext } from "../context/ProductContext";
import Header from "../layouts/header/Header";
import { useNavigation } from "../utils/navigation";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/footer/Footer";
import { useTranslation } from "react-i18next";

const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  background-color: var(--bg-100);
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--text-100);
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-200);
  margin-bottom: 20px;
`;

const fillLine = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;
const popDot = keyframes`
  0% { transform: scale(0); }
  70% { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

const StepperWrap = styled.div`
  width: 100%;
  max-width: 460px;
  margin: 10px 0 24px;
`;
const StepperTrack = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 10px;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 3px;
    background-color: var(--bg-300);
    transform: translateY(-50%);
    z-index: 0;
  }
`;
const StepperFill = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  height: 3px;
  background-color: var(--accent-100);
  transform: translateY(-50%);
  z-index: 1;
  width: 0%;
  animation: ${fillLine} 1.1s 0.3s ease-out forwards;
`;
const Dot = styled.div`
  position: relative;
  z-index: 2;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: var(--accent-100);
  color: var(--bg-100);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  animation: ${popDot} 0.4s ${(props) => props.$delay || "0s"} both;
`;
const StepLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 0.75rem;
  color: var(--text-200);
`;

const ReceiptCard = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: var(--bg-200);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px 0;
  @media (min-width: 1040px) {
    padding: 34px;
  }
`;

const ReceiptHead = styled.div`
  width: 94%;
  margin: 0 auto 10px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 2px dashed var(--bg-100);
  padding-bottom: 12px;
`;

const OrderIdText = styled.div`
  font-family: monospace;
  font-size: 1rem;
  color: var(--text-100);
`;

const ProductRow = styled.div`
  width: 94%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dotted var(--bg-100);

  &:last-of-type {
    border-bottom: none;
  }
`;
const ProductImg = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  background-color: var(--bg-100);
  flex-shrink: 0;
`;
const ProductName = styled.div`
  flex: 1;
  text-align: left;
  font-size: 0.9rem;
  color: var(--text-100);
`;
const ProductQty = styled.div`
  font-size: 0.85rem;
  color: var(--text-200);
`;
const ProductPrice = styled.div`
  font-size: 0.9rem;
  color: var(--text-100);
  min-width: 60px;
  text-align: right;
`;

const TotalsBlock = styled.div`
  width: 94%;
  margin: 12px auto 0;
  border-top: 2px dashed var(--bg-100);
  padding-top: 10px;
`;
const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${(props) => (props.$grand ? "1.05rem" : "0.9rem")};
  font-weight: ${(props) => (props.$grand ? "bold" : "normal")};
  color: var(--text-100);
  padding: 3px 0;
`;

const Button = styled.button`
  width: 94%;
  margin: 16px auto 0;
  display: block;
`;

const ThankYouPage = () => {
  const { t } = useTranslation();
  const { createdOrder, currencyTag } = useContext(ProductContext);
  const { goToOrderHistory } = useNavigation();
  const navigate = useNavigate();

  const products = createdOrder?.products || [];

  return (
    <>
      <Header />
      <ThankYouContainer>
        <Title>{t("THANK_YOU.TITLE")}</Title>
        <Subtitle>{t("THANK_YOU.SUBTITLE")}</Subtitle>

        <StepperWrap>
          <StepperTrack>
            <StepperFill />
            <Dot $delay="0.1s">1</Dot>
            <Dot $delay="0.5s">2</Dot>
            <Dot $delay="0.9s">3</Dot>
          </StepperTrack>
          <StepLabels>
            <span>{t("THANK_YOU.STEP_CONFIRMED", "Confirmed")}</span>
            <span>{t("THANK_YOU.STEP_PROCESSING", "Processing")}</span>
            <span>{t("THANK_YOU.STEP_SHIPPED", "Shipped")}</span>
          </StepLabels>
        </StepperWrap>

        {createdOrder && (
          <ReceiptCard>
            <ReceiptHead>
              <span>{t("THANK_YOU.ORDER_DETAILS")}</span>
              <OrderIdText>#{createdOrder.id}</OrderIdText>
            </ReceiptHead>

            {products.map((item, idx) => (
              <ProductRow key={idx}>
                {item.image && <ProductImg src={item.image} alt={item.name} />}
                <ProductName>{item.name}</ProductName>
                <ProductQty>x{item.quantity}</ProductQty>
                <ProductPrice>
                  {currencyTag}
                  {((item.discounted_price ?? item.price) * item.quantity).toFixed(2)}
                </ProductPrice>
              </ProductRow>
            ))}

            <TotalsBlock>
              <TotalRow>
                <span>{t("THANK_YOU.SUBTOTAL", "Subtotal")}</span>
                <span>
                  {currencyTag}
                  {createdOrder.subtotal}
                </span>
              </TotalRow>
              <TotalRow>
                <span>{t("THANK_YOU.SHIPPING", "Shipping")}</span>
                <span>
                  {currencyTag}
                  {createdOrder.shipping_cost}
                </span>
              </TotalRow>
              <TotalRow $grand>
                <span>{t("THANK_YOU.TOTAL", "Total")}</span>
                <span>
                  {currencyTag}
                  {createdOrder.total_price}
                </span>
              </TotalRow>
            </TotalsBlock>

            <Button onClick={() => navigate(goToOrderHistory())}>
              {t("THANK_YOU.VIEW_ORDER", "View Order")}
            </Button>
          </ReceiptCard>
        )}
      </ThankYouContainer>
      <Footer />
    </>
  );
};

export default ThankYouPage;
